import { Component, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { AI_CHAT_FEEDBACK_CATEGORIES, AiChatFeedbackCategory } from '@coreModels';

/** What the modal closes with when the customer submits. */
export interface AiChatFeedbackDetail {
  category: AiChatFeedbackCategory;
  note: string;
}

/**
 * Collects the detail behind a thumbs-down: which bucket the problem falls into and a note
 * saying what went wrong. Both are required - the feedback API rejects either one empty, and a
 * bucket with no note tells the review section nothing it can act on.
 *
 * Purely presentational: it owns no network call and no rating state. It closes with an
 * AiChatFeedbackDetail, or dismisses with nothing, and the caller decides what that means.
 * Mirrors the console's ChatFeedbackPopupComponent.
 */
@Component({
  selector: 'app-ai-chat-feedback-modal',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule],
  templateUrl: './ai-chat-feedback-modal.component.html',
  styleUrl: './ai-chat-feedback-modal.component.scss',
  // Required to reach the dropdown panel: ng-select appends it to <body>, so an emulated-
  // encapsulation attribute selector would never match it. Every rule in the stylesheet is
  // scoped by hand to compensate - see the note at the top of the file.
  encapsulation: ViewEncapsulation.None
})
export class AiChatFeedbackModalComponent {
  /* ################### Variable Declaration Start ################### */
  readonly categories = AI_CHAT_FEEDBACK_CATEGORIES;
  /** The API caps the note well above this; this is what a useful note needs, not the limit. */
  readonly maxNoteLength = 1000;

  feedbackForm: FormGroup;
  isSubmitted = false;
  /* ################### Variable Declaration End ################### */

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder
  ) {
    this.feedbackForm = this.formBuilder.group({
      category: [null, Validators.required],
      note: ['', [Validators.required, Validators.maxLength(this.maxNoteLength)]]
    });
  }

  /**
   * Validates, then closes with the detail. Marking touched on failure is what puts the error
   * messages on screen for a customer who submitted without filling anything in.
   */
  submit(): void {
    this.isSubmitted = true;

    if (this.feedbackForm.invalid) {
      this.feedbackForm.markAllAsTouched();
      return;
    }

    const detail: AiChatFeedbackDetail = {
      category: this.feedbackForm.value.category,
      note: (this.feedbackForm.value.note ?? '').trim()
    };
    this.activeModal.close(detail);
  }
}
